import { useState, type FormEvent } from 'react';
import { joinWaitlist } from '../../lib/waitlist';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function WaitlistBlock() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (status === 'submitting' || status === 'success') return;

    setStatus('submitting');
    setErrorMsg(null);

    const result = await joinWaitlist(email);

    if (result.ok) {
      setStatus('success');
    } else {
      setStatus('error');
      setErrorMsg(result.error);
    }
  }

  return (
    <section
      id="waitlist"
      className="border-t border-[rgba(43,36,20,0.18)] px-6 py-24 sm:py-28"
    >
      <div className="mx-auto flex max-w-xl flex-col items-center text-center">
        <h2
          className="m-0 text-[#2B2414] font-display"
          style={{
            fontWeight: 400,
            fontSize: 'clamp(24px, 3.2vw, 32px)',
            lineHeight: 1.2,
            letterSpacing: '-0.018em',
          }}
        >
          Launching summer 2026. Join the waitlist.
        </h2>

        {status === 'success' ? (
          <p
            className="mt-10 text-[#2B2414] font-body"
            style={{
              fontWeight: 400,
              fontSize: 16,
              letterSpacing: '-0.011em',
            }}
          >
            You're in. We'll email when it's time.
          </p>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="mt-10 flex w-full flex-col items-stretch justify-center gap-3 sm:flex-row"
            noValidate
          >
            <label htmlFor="waitlist-email" className="sr-only">
              Email address
            </label>
            <input
              id="waitlist-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={status === 'submitting'}
              className="flex-1 rounded-md border border-[rgba(43,36,20,0.18)] bg-transparent px-4 text-[15px] text-[#2B2414] placeholder:text-[#7B7162] focus:border-[#2B2414] focus:outline-none disabled:opacity-60 sm:max-w-[320px]"
              style={{
                fontWeight: 400,
                letterSpacing: '-0.011em',
                minHeight: 48,
              }}
            />
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="inline-flex items-center justify-center rounded-full bg-[#9E2D1B] px-8 text-[#F2ECE0] transition-colors duration-200 hover:bg-[#8A2717] disabled:opacity-60 font-body"
              style={{
                fontWeight: 500,
                fontSize: 15,
                letterSpacing: '-0.006em',
                minHeight: 48,
              }}
            >
              {status === 'submitting' ? 'Joining…' : 'Join'}
            </button>
          </form>
        )}

        {errorMsg && status === 'error' && (
          <p
            role="alert"
            className="mt-4 text-[#7E1B0B] font-body"
            style={{
              fontWeight: 400,
              fontSize: 13,
              letterSpacing: '-0.011em',
            }}
          >
            {errorMsg}
          </p>
        )}
      </div>
    </section>
  );
}
